package one.healthium.today.ui.med

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.times
import loco.GENERAL_CALORIES
import loco.GENERAL_CARBO
import loco.GENERAL_FAT
import loco.GENERAL_FIBRE
import loco.GENERAL_PROTEIN
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.orZero
import one.healthium.loco.resolve
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_7
import one.healthium.ui.compose.theme.yellow
import org.jetbrains.compose.resources.stringResource

@Composable
fun CaloriesCounter(
    modifier: Modifier = Modifier,
    model: CaloriesCounterUiModel?,
    onAddFoodClicked: () -> Unit,
) {
    Box(modifier) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 28.dp)
                .background(AppTheme.colors.surface, AppTheme.shapes.large),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(32.dp))
            CaloriesProgress(model?.caloriesGoal)
            Spacer(Modifier.height(12.dp))
            Row {
                Column {
                    CarboProgress(model?.carbohydratesGoal)
                    Spacer(Modifier.height(12.dp))
                    FatProgress(model?.fatGoal)
                }
                Spacer(Modifier.width(38.dp))
                Column {
                    ProteinProgress(model?.proteinGoal)
                    Spacer(Modifier.height(12.dp))
                    FibreProgress(model?.fiberGoal)
                }
            }
            Spacer(Modifier.height(39.dp))
        }
        AddButton(onClick = onAddFoodClicked)
    }
}

@Composable
private fun CaloriesProgress(goal: NutritionGoalUiModel?) {
    val animatedProgress by animateFloatAsState(
        targetValue = goal?.progress ?: 0f,
        animationSpec = tween(durationMillis = 400, delayMillis = 200),
    )

    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        ProgressIndicator(size = 147.dp, progress = animatedProgress)
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(8.dp))
            Text(
                text = goal?.amount.formatCalories(false).resolve(),
                style = AppTheme.textStyles.secondary_32_700,
                color = AppTheme.colors.text100,
            )
            Text(
                text = stringResource(loco.Res.string.GENERAL_CALORIES),
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.text100,
            )
        }
    }
}

@Composable
private fun CarboProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_CARBO))
}

@Composable
private fun ProteinProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_PROTEIN))
}

@Composable
private fun FatProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_FAT))
}

@Composable
private fun FibreProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_FIBRE))
}

@Composable
private fun BoxScope.AddButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(56.dp)
            .clip(CircleShape)
            .background(AppTheme.colors.primary, CircleShape)
            .align(Alignment.BottomCenter)
            .clickable { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = "+",
            style = AppTheme.textStyles.poppins32SemiBold,
            color = AppTheme.colors.textInverted100,
        )
    }
}

@Composable
private fun NutritionalGoalItem(
    goal: NutritionGoalUiModel?,
    name: String,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ProgressIndicator(size = 28.dp, progress = goal?.progress)
        Spacer(Modifier.width(12.dp))
        Column {
            Text(
                text = goal?.amount.orZero().formatGrams().resolve(),
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.text100,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                text = name,
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.text100.copy(alpha = 0.5f),
            )
        }
    }
}

@Composable
private fun ProgressIndicator(
    size: Dp,
    progress: Float?,
) {
    Box(
        modifier = Modifier
            .size(size)
            .background(gray_7, CircleShape)
            .clip(CircleShape),
        contentAlignment = Alignment.BottomCenter,
    ) {
        Box(Modifier.fillMaxWidth().background(yellow).height(progress?.times(size) ?: 0.dp))
    }
}
